import * as React from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import { useListAccessKeys } from 'src/features/accessKey/api/useListAccessKeys';
import CreateAccessKeyFormModal from 'src/features/accessKey/components/form/CreateAccessKeyFormModal';
import CellAccessKeyActions from 'src/features/accessKey/components/table/CellAccessKeyActions';
import { accessKeyTypeBadgeOptions } from 'src/features/accessKey/utils/constants';
import { accessKeyMessages } from 'src/features/accessKey/utils/messages';
import { AccessKey } from 'src/features/accessKey/utils/types';
import { PERMISSION } from 'src/features/auth';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { useParking } from 'src/providers/ParkingProvider';
import {
    CellBadge,
    CellDatetime,
    DataTable,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import { CellBoolean } from 'src/registry/components/table/cells/CellBoolean';
import DataTableColumnHeader from 'src/registry/components/table/DataTableColumnHeader';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import useFilters from 'src/registry/hooks/useFilters';
import usePagination from 'src/registry/hooks/usePagination';
import useSearch from 'src/registry/hooks/useSearch';
import useSorting from 'src/registry/hooks/useSorting';
import { useTitleStore } from 'src/stores/useTitleStore';
import { booleanBadgeOptions } from 'src/utils/constants';

const AccessKeyTable = () => {
    const { formatMessage } = useIntl();
    const { setTitle, removeTitle } = useTitleStore();
    const { parkingId } = useParking();

    const manageAccessKeys = useHasPermissions([PERMISSION.MANAGE_ACCESS_KEYS]);

    const { queryPaginationOptions, ...pagination } = usePagination();
    const { queryFiltersOptions, ...filters } = useFilters();
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'search' });
    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'value', desc: false }],
    });

    React.useEffect(() => {
        setTitle(formatMessage(accessKeyMessages.accessKeys));
        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const {
        data: listAccessKey,
        isLoading,
        isError,
    } = useListAccessKeys({
        parkingId,
        ...querySearchOptions,
        ...querySortingOptions,
        ...queryFiltersOptions,
        ...queryPaginationOptions,
    });

    const columnHelper = createColumnHelper<AccessKey>();
    const columns = [
        columnHelper.accessor('type', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            enableColumnFilter: true,
            cell: CellBadge,
            meta: {
                inputType: INPUT_TYPE.MULTI_SELECT,
                title: formatMessage(accessKeyMessages.type),
                badgeOptions: accessKeyTypeBadgeOptions,
            },
        }),
        columnHelper.accessor('value', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            meta: {
                title: formatMessage(accessKeyMessages.value),
            },
        }),
        columnHelper.accessor('valid_from', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            cell: CellDatetime,
            meta: {
                title: formatMessage(accessKeyMessages.validFrom),
            },
        }),
        columnHelper.accessor('valid_to', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            cell: CellDatetime,
            meta: {
                title: formatMessage(accessKeyMessages.validTo),
            },
        }),
        columnHelper.accessor('pass_back_allowed', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            enableColumnFilter: true,
            cell: CellBoolean,
            meta: {
                title: formatMessage(accessKeyMessages.passBackAllowed),
                badgeOptions: booleanBadgeOptions,
            },
        }),
        ...(manageAccessKeys
            ? [
                  columnHelper.display({
                      id: 'actions',
                      header: DataTableColumnHeader,
                      cell: CellAccessKeyActions,
                      maxSize: 45,
                  }),
              ]
            : []),
    ];

    return (
        <DataTable<AccessKey, any>
            pagination={pagination}
            isLoading={isLoading}
            isError={isError}
            columns={columns}
            data={listAccessKey}
            search={search}
            sorting={sorting}
            filters={filters}
            enableSearchParams={false}
            enableLocalFilters={false}
            semantics={{
                title: formatMessage(accessKeyMessages.accessKeys),
                entryName: formatMessage(accessKeyMessages.key).toLowerCase(),
                entryNames: formatMessage(accessKeyMessages.keys).toLowerCase(),
                placeholder: formatMessage(accessKeyMessages.value),
            }}
            getRowId={(row) => row.id.toString()}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(manageAccessKeys && { newEntry: CreateAccessKeyFormModal }),
            }}
        />
    );
};

export default AccessKeyTable;
